import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Tooltip
} from '@mui/material'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import { DateTimePicker } from '@mui/x-date-pickers'
import { useSmartTranslation } from './hooks/useSmartTranslation'
import { useDateFormatter } from './hooks/useDateFormatter'

const BETWEEN = 'between'
const DEFAULT_OPERATOR = 'gte'
// `between` is serialized as `iso1|iso2`. A pipe is used instead of a comma because the
// pagination request travels as a query string and URLSearchParams joins array entries
// with commas, which would split the value across two slots and break the parallel arrays.
const RANGE_DELIMITER = '|'

const buildOperators = (t_) => ([
  { value: 'gt', label: t_('After (greater than)') },
  { value: 'gte', label: t_('On or after') },
  { value: 'lt', label: t_('Before (less than)') },
  { value: 'lte', label: t_('On or before') },
  { value: BETWEEN, label: t_('Between') }
])

const toDate = (value) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return isNaN(date.getTime()) ? null : date
}

// Rebuild the local picker state from the value stored in the table filter (persistence).
const parseStoredValue = (operator, storedValue) => {
  if (!storedValue) {
    return operator === BETWEEN ? [null, null] : null
  }
  if (operator === BETWEEN) {
    const parts = String(storedValue).split(RANGE_DELIMITER)
    return [toDate(parts[0]), toDate(parts[1])]
  }
  return toDate(storedValue)
}

/**
 * Per-column date filter rendered in the table header. Opens a popover with an operator
 * selector and one (or two, for `between`) date-time pickers, mirroring the reference design.
 * On apply it serializes to ISO 8601 and hands `(field, operator, value)` to the table.
 */
const DateColumnFilterPopover = ({ column, storedValue, storedOperator, onApply, onClear }) => {
  const { t_ } = useSmartTranslation()
  const { format: dateFormat } = useDateFormatter()
  const operators = useMemo(() => buildOperators(t_), [t_])

  const [anchorEl, setAnchorEl] = useState(null)
  const [operator, setOperator] = useState(storedOperator || DEFAULT_OPERATOR)
  const [filterValue, setFilterValue] = useState(() => parseStoredValue(storedOperator || DEFAULT_OPERATOR, storedValue))

  const isActive = Boolean(storedValue)
  const open = Boolean(anchorEl)

  const handleOpen = (event) => {
    // Re-sync from the stored filter so reopening a filtered column recovers operator and dates.
    const currentOperator = storedOperator || DEFAULT_OPERATOR
    setOperator(currentOperator)
    setFilterValue(parseStoredValue(currentOperator, storedValue))
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)

  const handleOperatorChange = (event) => {
    const nextOperator = event.target.value
    setOperator(nextOperator)
    setFilterValue(nextOperator === BETWEEN ? [null, null] : null)
  }

  const canApply = operator === BETWEEN
    ? Array.isArray(filterValue) && Boolean(filterValue[0]) && Boolean(filterValue[1])
    : Boolean(filterValue)

  const handleApply = () => {
    let finalValue
    if (operator === BETWEEN) {
      const from = toDate(Array.isArray(filterValue) ? filterValue[0] : null)
      const to = toDate(Array.isArray(filterValue) ? filterValue[1] : null)
      if (!from || !to) return
      finalValue = `${from.toISOString()}${RANGE_DELIMITER}${to.toISOString()}`
    } else {
      const date = toDate(filterValue)
      if (!date) return
      finalValue = date.toISOString()
    }
    onApply(column.field, operator, finalValue)
    handleClose()
  }

  const handleClear = () => {
    setOperator(DEFAULT_OPERATOR)
    setFilterValue(null)
    onClear(column.field)
    handleClose()
  }

  return (
    <>
      <Tooltip title={t_('Filter')}>
        <IconButton size='small' onClick={handleOpen} color={isActive ? 'primary' : 'default'}>
          {isActive ? <FilterAltIcon fontSize='small' /> : <FilterAltOutlinedIcon fontSize='small' />}
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 290 }}>
          <Stack spacing={2}>
            <TextField
              select
              size='small'
              label={t_('Operator')}
              value={operator}
              onChange={handleOperatorChange}
              fullWidth
            >
              {operators.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>

            {operator === BETWEEN
              ? (
                <>
                  <DateTimePicker
                    label={t_('From')}
                    ampm={false}
                    format={dateFormat.dateTime}
                    value={Array.isArray(filterValue) ? filterValue[0] : null}
                    onChange={(date) => setFilterValue((prev) => [date, Array.isArray(prev) ? prev[1] : null])}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                  <DateTimePicker
                    label={t_('To')}
                    ampm={false}
                    format={dateFormat.dateTime}
                    value={Array.isArray(filterValue) ? filterValue[1] : null}
                    onChange={(date) => setFilterValue((prev) => [Array.isArray(prev) ? prev[0] : null, date])}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </>
                )
              : (
                <DateTimePicker
                  label={t_('Value')}
                  ampm={false}
                  format={dateFormat.dateTime}
                  value={Array.isArray(filterValue) ? null : filterValue}
                  onChange={(date) => setFilterValue(date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                )}

            <Stack direction='row' justifyContent='space-between'>
              <Button size='small' color='inherit' onClick={handleClear}>{t_('Clear')}</Button>
              <Button size='small' variant='contained' onClick={handleApply} disabled={!canApply}>{t_('Apply')}</Button>
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </>
  )
}

// Memoized: this lives in the table header and would otherwise re-render on every data
// refresh (the table refetches on an interval). Props are primitives plus a stable column
// reference and memoized callbacks, so it only re-renders when its own filter actually changes.
export default React.memo(DateColumnFilterPopover)
